<script>
  import { Line } from 'vue-chartjs'
  import moment from 'moment-es6'
  import * as actionTypes from '../../../store/action-types'
  import { mapGetters, mapState, mapActions } from 'vuex'
  import { GET_STUDENT_GRAPH_INTERACTIONS, HAS_FEATURE_FLAG } from '../../../store/getter-types'
  import Colors from '../../../mixins/Colors'
  import graphRequestTypes from '../../../store/graphRequestTypes'

  export default {
    extends: Line,
    name: 'EngagementGraph',
    mixins: [Colors],
    props: ['courseAverage', 'topPercent', 'exemptions', 'studentId', 'engagementGraphMode', 'dateRange', 'active'],
    data () {
      return {
        chartData: {},
        chartOptions: {},
        doAnimation: true,
        initiallyRendered: false,
        drawnExemptions: false
      }
    },
    methods: {
      ...mapActions({
        getStudentGraphData: actionTypes.GET_STUDENT_GRAPH_DATA
      }),
      fillData () {
        // Calc the step value so x axis labels are easy to read.
        let daysToDisplay = moment(this.dateRange.endDate).diff(moment(this.dateRange.startDate), 'days')
        let pixelsPerDay = this.$el.clientWidth / (daysToDisplay + 1)
        let step = 1
        for (let loop = 1; pixelsPerDay < 40; loop++) {
          step = loop * 7
          pixelsPerDay = this.$el.clientWidth * step / (daysToDisplay + 1)
        }

        let dateFormat = 'YYYY-MM-DD'
        this.chartData = {
          xLabels: this.$store.state.student.studentEngagement.dateLabels,
          interactions: this.graphInteractions
        }
        let animation = this.doAnimation ? {duration: 400} : {duration: 0}

        let displayEndDate = moment(this.dateRange.endDate).add(3, 'hours')
        this.chartOptions = {
          legend: {
            display: false
          },
          extraDraw: (ctx, chart, xAxis, yAxis) => { // Our callback
            this.drawExtra(ctx, chart, xAxis, yAxis)
          },
          animation,
          responsive: true,
          maintainAspectRatio: false,
          layout: {
            padding: {
              left: 0,
              right: 0,
              top: 11,
              bottom: 0
            }
          },
          scales: {
            xAxes: [
              {
                display: true,
                gridLines: {
                  display: true
                },
                type: 'time',
                distribution: 'linear',
                time: {
                  unit: 'day',
                  displayFormats: {
                    day: 'DD/MM'
                  },
                  tooltipFormat: 'DD/MM',
                  min: moment(this.dateRange.startDate, dateFormat),
                  max: moment(displayEndDate, dateFormat),
                  stepSize: step
                }
              }
            ]
          },
          tooltips: {
            callbacks: {
              label: (model, chart) => {
                const isBackgroundRange = chart.datasets[model.datasetIndex].isBackgroundRange
                return (this.isWeekByWeek && !isBackgroundRange) ? model.yLabel : ''
              }
            }
          }
        }

        if (this.isWeekByWeek) {
          this.chartData.yLabels = ['H', 'G', 'P', 'L', 'V', 'N']
          this.chartOptions.scales.yAxes = [{
            type: 'category',
            display: true,
            gridLines: {
              display: true
            },
            ticks: {
              reverse: true
            }
          }]
        } else {
          this.chartOptions.scales.yAxes = [{
            type: 'linear',
            display: false,
            gridLines: {
              display: true
            },
            ticks: {
              reverse: false,
              beginAtZero: false
            }
          }]
        }
      },
      makeAxis (source, color, dash) {
        return {
          data: source,
          fill: false,
          borderColor: color,
          borderDash: dash
        }
      },
      drawRatingLabels (ctx, xAxis, yAxis, ratingLetters, colors) {
        const x = xAxis.left - 13
        ctx.font = 'bold 12px roboto'
        for (let i = 0; i < ratingLetters.length; i++) {
          ctx.restore()
          ctx.beginPath()
          ctx.fillStyle = colors[i]
          let y = yAxis.getPixelForValue(ratingLetters[i])
          ctx.arc(x, y, 11, 0, Math.PI * 2, false)
          ctx.fill()
          ctx.fillStyle = '#ffffff'
          ctx.fillText(ratingLetters[i], x - 4, y + 4)
          ctx.save()
        }
      },
      drawExtra (ctx, chart, xAxis, yAxis) {
        if (this.isWeekByWeek) {
          this.drawRatingLabels(ctx, xAxis, yAxis,
              ['N', 'V', 'L', 'P', 'G', 'H'],
              ['#9E9E9E', '#F44336', '#FF9800', '#BA68C8', '#03A9F4', '#4CAF50'])
        }

        if (!this.drawnExemptions) {
          this.drawExemptions(ctx, chart, xAxis, yAxis)
          this.drawnExemptions = true
        }
      },
      drawExemptions (ctx, chart, xAxis, yAxis) {
        if (this.exemptions) {
          ctx.fillStyle = '#B3E5FC'
          ctx.globalAlpha = 0.5
          this.rangedStudentExemptions.forEach(exemption => {
            ctx.beginPath()
            const axisXstart = xAxis.getPixelForValue(exemption.startDate)
            const width = xAxis.getPixelForValue(exemption.endDate) - axisXstart
            ctx.fillRect(axisXstart, yAxis.bottom, width, yAxis.top - yAxis.bottom)
            ctx.stroke()
          })
          ctx.globalAlpha = 1.0
        }
      },
      displayChart () {
        this.fillData()
        this.chartData.datasets = [this.makeAxis(this.studentEngagementRating, this.materialColors.blue.base, [0, 0])]
        if (this.courseAverage) {
          this.chartData.datasets.push(this.makeAxis(this.courseAverageRating, this.materialColors.red.base, [3, 6]))
        }
        if (this.topPercent) {
          this.chartData.datasets.push(this.makeAxis(this.courseTopRating, this.materialColors.green.base, [7, 9]))
        }

        // Destroy any existing graph
        if (this.$data._chart) {
          this.drawnExemptions = false
          this.$data._chart.destroy()
        }
        this.renderChart(this.chartData, this.chartOptions)
      },
      getGraphComparisonData (average) {
        let dispatchNames = {
          true: {false: graphRequestTypes.GET_STUDENT_COURSE_TOP, true: graphRequestTypes.GET_STUDENT_COURSE_AVERAGE},
          false: {
            false: graphRequestTypes.GET_STUDENT_CUMULATIVE_COURSE_TOP,
            true: graphRequestTypes.GET_STUDENT_CUMULATIVE_COURSE_AVERAGE
          }
        }
        return dispatchNames[this.isWeekByWeek][average]
      },
      makeGraphRequest (graphs) {
        let graphData = {
          data: {
            studentId: this.studentId,
            cohortId: this.student.cohortId,
            startDate: this.dateRange.startDate,
            endDate: this.dateRange.endDate
          },
          graphs
        }
        this.getStudentGraphData(graphData)
      },
      loadGraphData () {
        this.initiallyRendered = true

        let engagement = this.isWeekByWeek ? graphRequestTypes.GET_STUDENT_ENGAGEMENT : graphRequestTypes.GET_STUDENT_CUMULATIVE_ENGAGEMENT
        let graphsRequired = [
          graphRequestTypes.FETCH_STUDENT_GRAPH_INTERACTIONS,
          engagement
        ]

        if (this.flagExemptionIndicator) {
          graphsRequired.push(graphRequestTypes.GET_STUDENT_EXEMPTIONS)
        }

        if (this.courseAverage) {
          graphsRequired.push(this.getGraphComparisonData(true))
        }
        if (this.topPercent) {
          graphsRequired.push(this.getGraphComparisonData(false))
        }
        this.makeGraphRequest(graphsRequired)
      },
      axisChange (isCourseAverage, show) {
        this.doAnimation = false
        if (show) {
          this.makeGraphRequest([this.getGraphComparisonData(isCourseAverage)])
        } else {
          this.displayChart()
        }
      }
    },
    computed: {
      isWeekByWeek () {
        return this.engagementGraphMode === 'weekByWeek'
      },
      rangedStudentExemptions () {
        /* Only return exemptions that overlap the displayed range, clipping
           so they don't exceed the displayed range.
         */
        const startMilli = moment(this.dateRange.startDate)
        const endMilli = moment(this.dateRange.endDate)
        return this.studentExemptions.filter(exemption => {
          return (exemption.startDate < endMilli) && (exemption.endDate > startMilli)
        }).map(exemption => {
          return {
            startDate: Math.max(exemption.startDate, startMilli),
            endDate: Math.min(exemption.endDate, endMilli)
          }
        })
      },
      ...mapState({
        studentEngagementRating: state => state.student.studentEngagement.rating,
        courseAverageRating: state => state.student.courseAverage.rating,
        courseTopRating: state => state.student.courseTop.rating,
        studentExemptions: state => state.student.studentExemptions,
        graphDataLoaded: state => state.student.graphDataLoaded,
        student: state => state.student.student,
        moduleInterExem: state => state.profile.module.interexem,
        functionStudentEngagement: state => state.student.functions.studentEngagement,
        optionExemptionIndicator: state => state.student.options.enable.exemptionIndicator
      }),
      ...mapGetters({
        graphInteractions: GET_STUDENT_GRAPH_INTERACTIONS,
        hasFeatureFlag: HAS_FEATURE_FLAG
      }),
      flagExemptionIndicator () {
        return this.hasFeatureFlag(this.moduleInterExem + '.' + this.functionStudentEngagement + '.' + this.optionExemptionIndicator)
      }
    },
    watch: {
      dateRange (dateRange) {
        // Date getRange has changed so re-query the data
        this.doAnimation = true
        this.loadGraphData()
      },
      courseAverage (courseAverage) {
        this.axisChange(true, this.courseAverage)
      },
      topPercent (topPercent) {
        this.axisChange(false, this.topPercent)
      },
      exemptions (exemptions) {
        this.axisChange(true, this.exemptions)
      },
      engagementGraphMode (graphMode) {
        this.doAnimation = true
        this.loadGraphData()
      },
      active (nowActive) {
        if (nowActive) {
          this.loadGraphData()
        }
      },
      graphInteractions (value) {
        this.doAnimation = false
        this.displayChart()
      },
      graphDataLoaded (value) {
        if (value) {
          this.displayChart()
        }
      }
    },
    mounted () {
      if (this.active) {
        this.loadGraphData()
      }
    }
  }
</script>



// WEBPACK FOOTER //
// src/components/student/engagement/EngagementGraph.vue